import { useState, useEffect } from 'react';
import { TeamName, useScore } from '../../../context/ScoreContext';
import PledgeModal from './PledgeModal';

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${8 + Math.floor((i * 37 + 13) % 84)}%`,
  top:  `${10 + Math.floor((i * 53 + 7)  % 80)}%`,
  size: 2 + (i % 3),
  delay: `${(i * 0.35) % 3.5}s`,
  duration: `${2.8 + (i % 4) * 0.6}s`,
  dx: `${((i % 5) - 2) * 14}px`,
  color: i % 2 === 0 ? '#d4a843' : '#888',
}));

export default function TeamSelectModal() {
  const { showTeamModal, setShowTeamModal, selectTeam, selectedTeam } = useScore();
  const [hovered, setHovered] = useState<TeamName | null>(null);
  const [selecting, setSelecting] = useState<TeamName | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [showPledge, setShowPledge] = useState(false);
  const [pendingTeam, setPendingTeam] = useState<TeamName | null>(null);

  useEffect(() => {
    if (showTeamModal) {
      const t = setTimeout(() => setShowContent(true), 60);
      return () => clearTimeout(t);
    } else {
      setShowContent(false);
    }
  }, [showTeamModal]);

  if (!showTeamModal) return null;

  const handleSelect = (team: TeamName) => {
    setSelecting(team);
    setTimeout(() => {
      setSelecting(null);
      setPendingTeam(team);
      setShowPledge(true);
    }, 420);
  };

  const handlePledgeConfirm = () => {
    if (pendingTeam) {
      selectTeam(pendingTeam);
    }
    setShowPledge(false);
    setPendingTeam(null);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">

        {/* Background */}
        <div className="absolute inset-0 bg-[#030303]" />
        <div
          className="absolute w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{
            top: '50%', left: '25%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(212,168,67,0.10) 0%, transparent 70%)',
            animation: 'modal-bg-pulse 4s ease-in-out infinite',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{
            top: '50%', left: '75%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(200,200,200,0.07) 0%, transparent 70%)',
            animation: 'modal-bg-pulse 4s 1.5s ease-in-out infinite',
          }}
        />
        {PARTICLES.map(p => (
          <div
            key={p.id}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: p.left, top: p.top,
              width: `${p.size}px`, height: `${p.size}px`,
              background: p.color,
              '--dx': p.dx,
              animation: `particle-float ${p.duration} ${p.delay} ease-in-out infinite`,
            } as React.CSSProperties}
          />
        ))}

        {/* Content wrapper */}
        <div className="relative z-10 min-h-full flex flex-col items-center justify-center px-3 py-4">

          <div className="w-full max-w-lg">

            {/* Title */}
            <div className={`text-center mb-3 transition-all duration-700 ${showContent ? 'modal-title-enter' : 'opacity-0'}`}>
              <div className="flex items-center justify-center gap-2 mb-1.5">
                <div className="h-px flex-1 max-w-[36px]" style={{ background: 'linear-gradient(90deg, transparent, #d4a843)' }} />
                <span className="text-[#d4a843] text-[8px] tracking-[0.3em] uppercase font-bold">Soul Resonance Battle</span>
                <div className="h-px flex-1 max-w-[36px]" style={{ background: 'linear-gradient(90deg, #d4a843, transparent)' }} />
              </div>
              <h1
                className="text-white font-black mb-1 leading-tight"
                style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(18px, 6vw, 40px)' }}
              >
                選擇你的陣營
              </h1>
              <p className="text-[#555] text-[10px] tracking-wider">一旦選擇，即為戰友 · 為榮耀而戰</p>
            </div>

            {/* Team cards — row on mobile too */}
            <div className="flex flex-row gap-2 sm:gap-3">

              {/* Black Team */}
              <button
                onClick={() => handleSelect('black')}
                onMouseEnter={() => setHovered('black')}
                onMouseLeave={() => setHovered(null)}
                onTouchStart={() => setHovered('black')}
                onTouchEnd={() => setHovered(null)}
                disabled={!!selecting}
                className={`card-enter-left flex-1 relative overflow-hidden rounded-2xl border-2 cursor-pointer transition-all duration-300 text-left
                ${hovered === 'black' || selecting === 'black' ? 'border-[#d4a843] scale-[1.01]' : 'border-[#2a2a2a]'}
                ${selecting === 'black' ? 'ring-2 ring-[#d4a843]/50' : ''}
              `}
                style={{
                  background: hovered === 'black' || selecting === 'black'
                    ? 'linear-gradient(145deg, #1c1500 0%, #0d0d0d 60%)'
                    : '#0f0f0f',
                  boxShadow: hovered === 'black' || selecting === 'black'
                    ? '0 0 30px rgba(212,168,67,0.2), inset 0 0 30px rgba(212,168,67,0.04)'
                    : 'none',
                }}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-20 pointer-events-none transition-opacity duration-300"
                  style={{
                    background: 'radial-gradient(ellipse at 50% -20%, rgba(212,168,67,0.18) 0%, transparent 70%)',
                    opacity: hovered === 'black' ? 1 : 0,
                  }}
                />

                <div className="relative px-3 py-4 sm:px-5 sm:py-5 flex flex-col items-center text-center">
                  {/* Icon */}
                  <div className="relative mb-2.5 sm:mb-4">
                    <div
                      className="absolute inset-0 rounded-full pointer-events-none"
                      style={{ margin: '-5px', border: '1px solid rgba(212,168,67,0.35)', animation: 'ring-pulse 2s ease-in-out infinite' }}
                    />
                    <div
                      className="relative w-11 h-11 sm:w-16 sm:h-16 flex items-center justify-center rounded-full border-2 transition-all duration-300"
                      style={{
                        borderColor: hovered === 'black' ? '#d4a843' : '#333',
                        background: hovered === 'black' ? 'radial-gradient(circle, #2a1e00 0%, #0d0d0d 80%)' : '#1a1a1a',
                        boxShadow: hovered === 'black' ? '0 0 16px rgba(212,168,67,0.4), inset 0 0 16px rgba(212,168,67,0.08)' : 'none',
                      }}
                    >
                      <i
                        className="ri-shield-fill text-xl sm:text-3xl transition-all duration-300"
                        style={{
                          color: hovered === 'black' ? '#d4a843' : '#444',
                          filter: hovered === 'black' ? 'drop-shadow(0 0 8px #d4a843aa)' : 'none',
                        }}
                      />
                    </div>
                  </div>

                  {/* Badge */}
                  <div
                    className="text-[7px] sm:text-[9px] tracking-[0.3em] uppercase font-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full mb-1.5 sm:mb-2 transition-all duration-300"
                    style={{
                      background: hovered === 'black' ? '#d4a843' : '#1e1e1e',
                      color: hovered === 'black' ? '#0d0d0d' : '#555',
                    }}
                  >
                    BLACK TEAM
                  </div>

                  <h2 className="text-white text-lg sm:text-2xl font-black mb-1 sm:mb-1.5" style={{ fontFamily: 'Georgia, serif' }}>
                    黑隊
                  </h2>
                  <p className="text-[10px] sm:text-xs leading-relaxed mb-3 sm:mb-4" style={{ color: hovered === 'black' ? '#aaa' : '#555' }}>
                    暗夜中凝聚力量<br />以沉穩之力，覆蓋一切
                  </p>

                  <div
                    className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs font-bold tracking-widest px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl transition-all duration-300 w-full justify-center"
                    style={{
                      background: hovered === 'black' ? '#d4a843' : '#1a1a1a',
                      color: hovered === 'black' ? '#0d0d0d' : '#444',
                      border: hovered === 'black' ? 'none' : '1px solid #2a2a2a',
                      opacity: hovered === 'black' ? 1 : 0.6,
                    }}
                  >
                    {selecting === 'black'
                      ? <><i className="ri-loader-4-line animate-spin" /> 加入中…</>
                      : <><i className="ri-arrow-right-circle-fill" /> 加入黑隊</>}
                  </div>
                </div>
              </button>

              {/* VS */}
              <div className="flex items-center justify-center self-center shrink-0 vs-enter">
                <div
                  className="relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: 'radial-gradient(circle, #1e1508 0%, #0d0d0d 70%)',
                    border: '2px solid rgba(212,168,67,0.5)',
                    boxShadow: '0 0 16px rgba(212,168,67,0.3)',
                  }}
                >
                  <span
                    className="text-xs sm:text-sm font-black"
                    style={{
                      color: '#d4a843',
                      fontFamily: 'Georgia, serif',
                      textShadow: '0 0 8px #d4a843',
                    }}
                  >
                    VS
                  </span>
                </div>
              </div>

              {/* White Team */}
              <button
                onClick={() => handleSelect('white')}
                onMouseEnter={() => setHovered('white')}
                onMouseLeave={() => setHovered(null)}
                onTouchStart={() => setHovered('white')}
                onTouchEnd={() => setHovered(null)}
                disabled={!!selecting}
                className={`card-enter-right flex-1 relative overflow-hidden rounded-2xl border-2 cursor-pointer transition-all duration-300 text-left
                ${hovered === 'white' || selecting === 'white' ? 'border-[#d0d0d0] scale-[1.01]' : 'border-[#2a2a2a]'}
                ${selecting === 'white' ? 'ring-2 ring-white/30' : ''}
              `}
                style={{
                  background: hovered === 'white' || selecting === 'white'
                    ? 'linear-gradient(145deg, #1c1c1c 0%, #0d0d0d 60%)'
                    : '#0f0f0f',
                  boxShadow: hovered === 'white' || selecting === 'white'
                    ? '0 0 30px rgba(220,220,220,0.12), inset 0 0 30px rgba(220,220,220,0.03)'
                    : 'none',
                }}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-20 pointer-events-none transition-opacity duration-300"
                  style={{
                    background: 'radial-gradient(ellipse at 50% -20%, rgba(220,220,220,0.13) 0%, transparent 70%)',
                    opacity: hovered === 'white' ? 1 : 0,
                  }}
                />

                <div className="relative px-3 py-4 sm:px-5 sm:py-5 flex flex-col items-center text-center">
                  <div className="relative mb-2.5 sm:mb-4">
                    <div
                      className="absolute inset-0 rounded-full pointer-events-none"
                      style={{ margin: '-5px', border: '1px solid rgba(220,220,220,0.25)', animation: 'ring-pulse 2s 1s ease-in-out infinite' }}
                    />
                    <div
                      className="relative w-11 h-11 sm:w-16 sm:h-16 flex items-center justify-center rounded-full border-2 transition-all duration-300"
                      style={{
                        borderColor: hovered === 'white' ? '#d0d0d0' : '#333',
                        background: hovered === 'white' ? 'radial-gradient(circle, #222 0%, #0d0d0d 80%)' : '#1a1a1a',
                        boxShadow: hovered === 'white' ? '0 0 16px rgba(220,220,220,0.25), inset 0 0 16px rgba(220,220,220,0.06)' : 'none',
                      }}
                    >
                      <i
                        className="ri-sword-fill text-xl sm:text-3xl transition-all duration-300"
                        style={{
                          color: hovered === 'white' ? '#e8e8e8' : '#444',
                          filter: hovered === 'white' ? 'drop-shadow(0 0 8px #ffffffaa)' : 'none',
                        }}
                      />
                    </div>
                  </div>

                  <div
                    className="text-[7px] sm:text-[9px] tracking-[0.3em] uppercase font-black px-2 py-0.5 sm:px-3 sm:py-1 rounded-full mb-1.5 sm:mb-2 transition-all duration-300"
                    style={{
                      background: hovered === 'white' ? '#e8e8e8' : '#1e1e1e',
                      color: hovered === 'white' ? '#0d0d0d' : '#555',
                    }}
                  >
                    WHITE TEAM
                  </div>

                  <h2 className="text-white text-lg sm:text-2xl font-black mb-1 sm:mb-1.5" style={{ fontFamily: 'Georgia, serif' }}>
                    白隊
                  </h2>
                  <p className="text-[10px] sm:text-xs leading-relaxed mb-3 sm:mb-4" style={{ color: hovered === 'white' ? '#aaa' : '#555' }}>
                    光明中淬煉銳氣<br />以純粹之刃，斬破黑暗
                  </p>

                  <div
                    className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs font-bold tracking-widest px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl transition-all duration-300 w-full justify-center"
                    style={{
                      background: hovered === 'white' ? '#e8e8e8' : '#1a1a1a',
                      color: hovered === 'white' ? '#0d0d0d' : '#444',
                      border: hovered === 'white' ? 'none' : '1px solid #2a2a2a',
                      opacity: hovered === 'white' ? 1 : 0.6,
                    }}
                  >
                    {selecting === 'white'
                      ? <><i className="ri-loader-4-line animate-spin" /> 加入中…</>
                      : <><i className="ri-arrow-right-circle-fill" /> 加入白隊</>}
                  </div>
                </div>
              </button>
            </div>

            <p className="text-center text-[#2a2a2a] text-[9px] sm:text-[10px] mt-3 sm:mt-4 tracking-wide">
              每次開啟頁面皆可重新選擇隊伍 · 選定後中途無法自行更換
            </p>
          </div>
        </div>

        <PledgeModal show={showPledge} team={pendingTeam} onConfirm={handlePledgeConfirm} />
      </div>
    </>
  );
}
